import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase/client.js';

import { ForgotPasswordModal } from './forgot-password';
import AnimatedShaderBackground from './ui/AnimatedShaderBackground';
import FluidGeometryBackground from './ui/FluidGeometryBackground';
import RotatingCube from './ui/RotatingCube';
import AudioPlayer from './ui/AudioPlayer';

// ============================================================================
// 背景效果切换配置 (可逆测试)
// ============================================================================
// 设置为 true 使用新的流体几何形状效果
// 设置为 false 使用原来的流星效果
// ============================================================================
const USE_FLUID_GEOMETRY_EFFECT = true; // 切换这个值来测试不同效果

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 强制清除任何可能的默认值
  React.useEffect(() => {
    // 延迟执行，确保DOM已渲染
    const timer = setTimeout(() => {
      const emailInput = document.getElementById('email');
      const passwordInput = document.getElementById('password');

      if (emailInput && emailInput.value !== '') {
        emailInput.value = '';
        setFormData(prev => ({ ...prev, email: '' }));
      }

      if (passwordInput && passwordInput.value !== '') {
        passwordInput.value = '';
        setFormData(prev => ({ ...prev, password: '' }));
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // 登錄成功，獲取 session 並跳轉到用戶的個人頁面
        setErrorMessage('');

        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session?.user?.id) {
            // 跳轉到用戶的個人頁面
            navigate(`/artist/${session.user.id}`);
          } else {
            // 如果沒有 session，跳轉到首頁
            navigate('/');
          }
        } catch (error) {
          console.warn('Failed to get session, redirecting to home:', error);
          navigate('/');
        }
      } else {
        // 登錄失敗，顯示錯誤信息
        console.error('Login failed:', result.error);
        // 将 "Invalid login credentials" 替换为更友好的提示
        const displayError =
          result.error === 'Invalid login credentials'
            ? 'Incorrect email or password'
            : result.error || 'Login failed. Please try again.';
        setErrorMessage(displayError);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-[106.3vh] bg-gray-950 flex flex-col relative'>
      {/* 背景動畫 - 深層背景 */}
      {/* 可切換：流星效果 或 流体几何形状效果 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        {USE_FLUID_GEOMETRY_EFFECT ? (
          <FluidGeometryBackground />
        ) : (
          <AnimatedShaderBackground />
        )}
      </div>

      {/* 旋轉立方體動畫 - 淺層裝飾 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1 }}>
        <RotatingCube />
      </div>

      {/* 音頻播放器 - 右上角 */}
      <AudioPlayer />

      {/* Main Content - 添加 relative z-10 確保在所有背景動畫上方 */}
      <div className='relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8'>
        {/* 宽容器，标题可铺满屏宽 */}
        <div className='w-full'>
          {/* TAG Logo - Above slogan */}
          <div className='text-center mb-6'>
            <img
              src={`${process.env.PUBLIC_URL}/TAG_Logo.png`}
              alt='TAG Logo'
              className='h-12 w-auto mx-auto mb-4'
            />
            <h1 className='text-lg sm:text-3xl font-black mb-1'>
              <span className='inline-flex flex-nowrap items-baseline gap-2 whitespace-nowrap'>
                <span className='bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent'>
                  Show your work.
                </span>
                <span className='bg-gradient-to-r from-purple-500 to-purple-600 bg-clip-text text-transparent'>
                  Find your team.
                </span>
              </span>
            </h1>
          </div>

          {/* 卡片保持窄宽 */}
          <div className='max-w-sm w-full mx-auto space-y-4'>
            {/* Login Card */}
            <div className='bg-transparent rounded-lg p-4 sm:p-6'>
              {/* Login Form */}
              <form onSubmit={handleSubmit} className='space-y-4 sm:space-y-5'>
                {/* Email Field */}
                <div>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <svg
                        className='h-5 w-5 text-gray-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                        />
                      </svg>
                    </div>
                    <input
                      id='email'
                      name='email'
                      type='email'
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder='Student Email'
                      autoComplete='off'
                      className='block w-full pl-8 sm:pl-10 pr-3 py-3 sm:py-2.5 border-t border-b border-white rounded-none leading-6 min-h-[44px] bg-transparent placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-tag-blue focus:border-tag-blue text-sm'
                      style={{
                        minHeight: '44px',
                        lineHeight: '1.5',
                      }}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor='password'
                    className='block text-xs sm:text-sm font-light text-white mb-1 sm:mb-2'
                  >
                    Password
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <svg
                        className='h-5 w-5 text-gray-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                        />
                      </svg>
                    </div>
                    <input
                      id='password'
                      name='password'
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder='••••••••'
                      autoComplete='off'
                      className='block w-full pl-8 sm:pl-10 pr-10 py-3 sm:py-2.5 border-t border-b border-white rounded-none leading-6 min-h-[44px] bg-transparent placeholder-gray-400 text-white focus:outline-none focus:ring-tag-blue focus:border-tag-blue text-sm'
                      style={{
                        minHeight: '44px',
                        lineHeight: '1.5',
                      }}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-3 flex items-center'
                    >
                      {showPassword ? (
                        <svg
                          className='h-5 w-5 text-gray-400 hover:text-white transition-colors'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M3 3l18 18'
                          />
                        </svg>
                      ) : (
                        <svg
                          className='h-5 w-5 text-gray-400 hover:text-white transition-colors'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className='rounded-md bg-red-50 p-4'>
                    <div className='flex'>
                      <div className='flex-shrink-0'>
                        <svg
                          className='h-5 w-5 text-red-400'
                          viewBox='0 0 20 20'
                          fill='currentColor'
                        >
                          <path
                            fillRule='evenodd'
                            d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                      <div className='ml-3'>
                        <p className='text-sm text-red-700'>{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remember Me and Forgot Password */}
                <div className='flex items-center justify-between'>
                  <div className='flex items-center'>
                    <input
                      id='rememberMe'
                      name='rememberMe'
                      type='checkbox'
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      className='h-4 w-4 text-tag-blue focus:ring-tag-blue border-2 border-white bg-transparent rounded appearance-none checked:bg-tag-blue checked:border-tag-blue'
                    />
                    <label
                      htmlFor='rememberMe'
                      className='ml-2 block text-sm font-light text-white'
                    >
                      Remember me
                    </label>
                  </div>
                  <div className='text-sm'>
                    <button
                      type='button'
                      onClick={() => setIsForgotPasswordOpen(true)}
                      className='font-light text-white hover:text-tag-blue'
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* Sign In Button */}
                <button
                  type='submit'
                  disabled={isLoading}
                  className='w-full flex justify-center py-2 sm:py-2.5 px-4 border border-white rounded-md shadow-sm text-sm font-light text-white bg-black hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tag-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                >
                  {isLoading ? (
                    <svg
                      className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                  ) : null}
                  {isLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>
            </div>

            {/* Sign Up Card */}
            <div className='bg-transparent rounded-lg p-4 sm:p-6'>
              <p className='text-sm font-light text-center mb-4 bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent'>
                New here? Please Sign Up first.
              </p>
              <div className='flex justify-center'>
                <Link
                  to='/register'
                  className='w-full flex justify-center py-2 sm:py-2.5 px-4 border border-white rounded-md text-sm font-light text-white bg-transparent hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tag-blue transition-all duration-200'
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className='bg-gradient-to-r from-blue-500 to-purple-600 hidden md:block relative z-10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <div className='flex justify-between items-center'>
            <div className='text-sm text-white'>
              <span className='font-bold'>TAG Art Guide</span>
              <span className='ml-2'>© 2025 TAG. All rights reserved.</span>
            </div>
            <div className='flex space-x-6 text-sm text-white'>
              <a
                href='/privacy'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Privacy Policy
              </a>
              <a
                href='/terms'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Terms of Service
              </a>
              <a
                href='/help'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Help Center
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
      />
    </div>
  );
};

export default LoginPage;
